import { ACTION } from '../../types';
import apiCaller, { wrap } from '../../utils/ApiClient';
import * as constants from './constants';
import { setLoading } from '../ui/actions';
import { objectToSearchParams } from '../../utils/toSearchParams';
import { AppState } from '../../store';
import { parseProductUrnFromSubProductUrn } from '../../utils/products';
import { REMOVE_BASKET_DATA } from './constants';

export const postSelect = (payload: any, signal?: any) =>
  wrap(async (dispatch: (arg0: ACTION) => void, getState: () => AppState) => {
    try {
      dispatch({ type: constants.START_FETCH_PRODUCTS });
      let types = ['it21/dn', 'it21/dnk'];
      if (
        payload.criteria?.coordinate &&
        payload.criteria?.coordinate.length === 0 &&
        !payload.types &&
        payload.criteria?.searchurn &&
        payload.criteria?.searchurn.length === 0
      ) {
        types = ['it21/dn'];
      }
      payload.types = types;

      const resData = await apiCaller('POST', `select`, payload, {}, signal);
      if (!resData) {
        return 'Aborting';
      }
      const locations = getState().locations;
      const industries = locations.searchParameters.industries;
      dispatch({
        type: constants.FETCH_PRODUCTS,
        payload: resData,
        industries: industries,
        usedTypes: types,
        usedTimeslot: payload.criteria && payload.criteria.timeslot ? payload.criteria.timeslot : 1,
      });

      if (types.length === 1 && types[0] === 'it21/dn') {
        payload.types.push('it21/dnk');
        dispatch(postSelect(payload, signal));
      }

      return resData;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return 'Aborting';
      }
      console.error('error: ', err);
      throw err;
    }
  }, 'POST_ERROR');

export const fetchProductByUrn = (urn, date?, duration?) =>
  wrap((dispatch) => {
    try {
      const paramsObj = { urn, date, duration };
      const parsedParams = objectToSearchParams(paramsObj, true).toString();

      return apiCaller('GET', `product?${parsedParams}`).then((resData) => resData);
    } catch (err) {
      console.error('error: ', err);
      throw err;
    }
  }, 'FETCH_ERROR');

export const fetchBookingDates = () =>
  wrap(async (dispatch: (arg0: ACTION) => void) => {
    try {
      dispatch(setLoading('general', true));
      const resData = await apiCaller('GET', `possiblebookingdates`);
      dispatch({ type: constants.FETCH_BOOKING_DATES, payload: resData });
      dispatch(setLoading('general', false));
      return resData;
    } catch (err) {
      console.error('error: ', err);
      dispatch(setLoading('general', false));
      throw err;
    }
  }, 'FETCH_ERROR');

export const fetchIndustriesBig5 = () =>
  wrap(async (dispatch: (arg0: ACTION) => void) => {
    try {
      dispatch(setLoading('general', true));
      const resData = await apiCaller('GET', `industry?context=big5`);
      dispatch({
        type: constants.FETCH_INDUSTRIES_BIG5,
        payload: resData,
      });
      dispatch(setLoading('general', false));
      return resData;
    } catch (err) {
      console.error('error: ', err);
      dispatch(setLoading('general', false));
      throw err;
    }
  }, 'FETCH_ERROR');

export const setProductFilterData = (producturn, payload) => {
  return (dispatch) => {
    dispatch({
      type: constants.SET_PRODUCT_FILTER_DATA,
      payload: {
        producturn: producturn,
        data: payload,
      },
    });
  };
};

export const clearProductFilterData = () => ({
  type: constants.CLEAR_PRODUCT_FILTER_DATA,
  payload: {},
});

export const deleteBasket = (payload) =>
  wrap((dispatch) => {
    try {
      dispatch(setLoading('basket', true));
      const parameters = new URLSearchParams(payload);
      return apiCaller('DELETE', `basketdelete?` + parameters.toString()).then((resData) => {
        dispatch(setLoading('basket', false));

        return resData;
      });
    } catch (err) {
      console.error('error: ', err);
      dispatch(setLoading('basket', false));
      throw err;
    }
  }, constants.FETCH_ERROR);

export const addToBasket = (payload) =>
  wrap((dispatch) => {
    dispatch(setLoading('basket', true));

    return apiCaller('POST', `addtobasket`, payload)
      .then((resData) => {
        dispatch({ type: constants.ADD_TO_BASKET, payload: resData });
        dispatch(setLoading('basket', false));
        return resData;
      })
      .catch((err) => {
        console.error('error: ', err);
        dispatch(setLoading('basket', false));
        throw err;
      });
  });

export const removeFromBasket = (payload) =>
  wrap((dispatch) => {
    dispatch(setLoading('basket', true));
    return new Promise((resolve, reject) => {
      try {
        return apiCaller('POST', `removefrombasket`, payload).then(
          (resData) => {
            dispatch(setLoading('basket', false));
            resolve(resData);
          },
          (error) => {
            dispatch(setLoading('basket', false));
            reject(error);
          },
        );
      } catch (err) {
        console.error('error: ', err);
        dispatch(setLoading('basket', false));
        reject(err);
      }
    });
  }, 'POST_ERROR');

export const createBasket = (payload) =>
  wrap((dispatch) => {
    try {
      dispatch(setLoading('basket', true));
      return apiCaller('POST', `newbasket`, payload).then((resData) => {
        dispatch({ type: constants.CREATE_BASKET, payload: resData });
        dispatch(setLoading('basket', false));
        return resData;
      });
    } catch (err) {
      console.error('error: ', err);
      dispatch(setLoading('basket', false));
      throw err;
    }
  }, 'POST_ERROR');

export const openBasket = () => ({ type: constants.OPEN_BASKET });
export const closeBasket = () => ({ type: constants.CLOSE_BASKET });
export const clearBasket = () => ({ type: constants.CLEAR_BASKET });
export const removeBasketData = () => ({ type: constants.REMOVE_BASKET_DATA });

export const getBasketContent = (payload) =>
  wrap((dispatch) => {
    try {
      dispatch(setLoading('basket', true));
      if (payload.basketreference === null) {
        delete payload.basketreference;
      }

      delete payload.cancel;
      delete payload.state;

      const parameters = new URLSearchParams({
        ...payload,
        group: 'producttype,city,format',
      });

      return apiCaller('GET', `basketcontent?` + parameters.toString()).then((resData) => {
        dispatch(setLoading('basket', false));
        dispatch({
          type: constants.FETCH_BASKET,
          payload: { ...resData, basket: payload.basket },
        });
        return resData['data'];
      });
    } catch (err) {
      console.error('error: ', err);
      dispatch(setLoading('basket', false));
      throw err;
    }
  }, constants.FETCH_ERROR);

export const resetAllProducts = () => ({
  type: constants.RESET_ALL,
});

export const updateBatchEditFailedCount = (count: number) => ({
  type: constants.UPDATE_BATCH_EDIT_FAILED_COUNT,
  payload: count,
});

export const getPriceForProduct = (payload) =>
  wrap((dispatch, getState) => {
    try {
      let actualProductUrn = payload?.producturn;
      if (payload.producturn?.indexOf(':subrow:') > -1) {
        const { producturn } = parseProductUrnFromSubProductUrn(actualProductUrn);
        payload = { ...payload, subproducturn: actualProductUrn };
        actualProductUrn = producturn;
      }

      const parameters = new URLSearchParams({
        ...payload,
        producturn: actualProductUrn,
      });
      return apiCaller('GET', `priceforproduct?` + parameters.toString())
        .then((resData) => {
          dispatch({
            type: constants.FETCHED_PRODUCT_PRICE,
            payload: {
              ...resData,
              subproducturn: payload.producturn,
            },
          });
          return resData;
        })
        .catch((err) => {
          const state = getState();
          const count = state?.products?.batchEditFailedCount ? state.products.batchEditFailedCount + 1 : 1;
          dispatch({
            type: constants.UPDATE_BATCH_EDIT_FAILED_COUNT,
            payload: count,
          });
        });
    } catch (err) {
      console.error('error: ', err);
      throw err;
    }
  }, 'POST_ERROR');

export const calculateShareOfTime = (payload) =>
  wrap((dispatch) => {
    try {
      let actualProductUrn = payload?.producturn;
      if (payload.producturn?.indexOf(':subrow:') > -1) {
        const { producturn } = parseProductUrnFromSubProductUrn(actualProductUrn);
        payload = { ...payload, subproducturn: actualProductUrn };
        actualProductUrn = producturn;
      }

      const parameters = new URLSearchParams({
        ...payload,
        producturn: actualProductUrn,
      });
      return apiCaller('GET', `calculateshareoftime?` + parameters.toString()).then((resData) => {
        dispatch({
          type: constants.CALCULATE_SHARE_OF_TIME,
          payload: {
            ...resData,
            subproducturn: payload.producturn,
          },
        });

        return resData;
      });
    } catch (err) {
      console.error('error: ', err);
      throw err;
    }
  }, 'POST_ERROR');

export const getFilters = () =>
  wrap((dispatch) => {
    try {
      return apiCaller('GET', `productfilter`).then((resData) => {
        dispatch({
          type: constants.GET_FILTERS,
          payload: {
            ...resData,
          },
        });
        return resData;
      });
    } catch (err) {
      console.error('error: ', err);
      throw err;
    }
  }, 'POST_ERROR');

export const updateBasketDefinition = (payload) =>
  wrap((dispatch) => {
    try {
      dispatch(setLoading('basket', true));
      return apiCaller('PATCH', `basketdefinition`, payload).then((resData) => {
        dispatch({
          type: constants.UPDATE_BASKET_DEFINITION,
          payload: resData,
        });
        dispatch(setLoading('basket', false));
        return resData;
      });
    } catch (err) {
      console.error('error: ', err);
      dispatch(setLoading('basket', false));
      throw err;
    }
  }, 'POST_ERROR');

export const updateProductBasketEntries = (payload) =>
  wrap((dispatch) => {
    try {
      dispatch(setLoading('basket', true));
      return apiCaller('PATCH', `basketentry`, payload).then((resData) => {
        dispatch({
          type: constants.UPDATE_PRODUCT_BASKET_ENTRY,
          payload: resData,
        });
        dispatch(setLoading('basket', false));
        return resData;
      });
    } catch (err) {
      console.error('error: ', err);
      dispatch(setLoading('basket', false));
      throw err;
    }
  }, 'POST_ERROR');

export const updateBasketData = (payload) =>
  wrap((dispatch) => {
    try {
      dispatch(setLoading('basket', true));
      return apiCaller('POST', `basketdata`, payload).then((resData) => {
        dispatch({
          type: constants.UPDATE_BASKET_DATA,
          payload: resData,
        });
        dispatch(setLoading('basket', false));
        return resData;
      });
    } catch (err) {
      console.error('error: ', err);
      dispatch(setLoading('basket', false));
      throw err;
    }
  }, 'POST_ERROR');

export const getAvailabilityForDigitalProduct = (payload) =>
  wrap((dispatch) => {
    try {
      const parameters = new URLSearchParams(payload);
      return apiCaller('GET', `digitalavailability?` + parameters.toString()).then((resData) => {
        dispatch({
          type: constants.FETCHED_DIGITAL_PRODUCT_AVAILABILITY,
          payload: resData,
        });
        return resData;
      });
    } catch (err) {
      console.error('error: ', err);
      throw err;
    }
  }, 'POST_ERROR');

export const setStartDate = (startDate) => ({
  type: 'SET_START_DATE',
  payload: startDate,
});

export const setDuration = (duration) => ({
  type: 'SET_DURATION',
  payload: duration,
});

export const batchSelectGroup = (payload) => (dispatch, getState) => {
  const { groupId, productIds } = payload;
  const state = getState();
  if (state.products.batchSelect.selectedGroupsIds.includes(groupId)) {
    return;
  }

  dispatch({
    type: constants.BATCH_SELECT_GROUP,
    payload: { groupId, productIds: productIds },
  });
};

export const batchDeselectGroup = (payload) => (dispatch, getState) => {
  const { groupId, productIds } = payload;
  const state = getState();
  if (!state.products.batchSelect.selectedGroupsIds.includes(groupId)) {
    return;
  }
  dispatch({
    type: constants.BATCH_DESELECT_GROUP,
    payload: { groupId, productIds },
  });
};

export const batchSelectProduct = (productUrn) => (dispatch, getState) => {
  const state = getState();
  if (state.products.batchSelect.selectedProductUrns.includes(productUrn)) {
    return;
  }

  dispatch({
    type: constants.BATCH_SELECT_PRODUCT,
    payload: productUrn,
  });
};

export const batchDeselectProduct = (productUrn) => (dispatch, getState) => {
  const state = getState();
  const { selectedProductUrns, selectedGroupsIds } = state.products.batchSelect;

  if (!selectedProductUrns.includes(productUrn)) {
    return;
  }
  const parentGroup = state.products.productsByUrn[productUrn].subformat;
  dispatch({
    type: constants.BATCH_DESELECT_PRODUCT,
    payload: productUrn,
  });
  if (parentGroup && selectedGroupsIds.includes(parentGroup)) {
    dispatch({
      type: constants.BATCH_DESELECT_GROUP,
      payload: {
        groupId: parentGroup,
        productIds: [productUrn],
      },
    });
  }
};

export const deselectAllProductsAndGroups = () => {
  return {
    type: constants.DESELECT_ALL_PRODUCTS_AND_GROUPS,
  };
};

export const filteringProducts = (payload) => {
  return {
    type: constants.FILTERING_PRODUCTS,
    payload,
  };
};

export const clearFilteredProducts = () => {
  return {
    type: constants.CLEAR_FILTERED_PRODUCTS,
  };
};

export const clearFilteredProductsByType = (type) => {
  return {
    type: constants.CLEAR_FILTERED_PRODUCTS_BY_TYPE,
    payload: type,
  };
};

export const selectAllFilteredProductsByType = (payload) => {
  return {
    type: constants.SELECT_ALL_FILTERED_PRODUCTS_BY_TYPE,
    payload: payload,
  };
};

export const receivedNotice = (payload: any): ACTION => ({
  type: constants.NOTICE_RECEIVED,
  payload,
});

export const addAbortController = (payload: any): ACTION => ({
  type: constants.ADD_ABORT_CONTROLLER,
  payload,
});
